import { useEffect, useState } from 'react';
import { useClientApi } from '@/hooks/useClientApi';
import { Client } from '@/types/client';

interface UseSchemeClientsResult {
  /** Every client of each loaded scheme, keyed by scheme id. */
  schemeClients: Record<number, Client[]>;
  loading: boolean;
}

/**
 * Loads the clients of each given scheme for the order forms' client pickers.
 * A scheme is fetched once and kept, so toggling schemes on and off does not refetch.
 */
export function useSchemeClients(schemeIds: number[]): UseSchemeClientsResult {
  const { getClients } = useClientApi();
  const [schemeClients, setSchemeClients] = useState<Record<number, Client[]>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const schemesToFetch = schemeIds.filter(id => !(id in schemeClients));
    if (schemesToFetch.length === 0) {
      // A fetch cancelled by this change never clears its own loading flag.
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchAllClientsForScheme = async (schemeId: number) => {
      const clients: Client[] = [];
      let page = 1;
      let hasNext = true;
      while (hasNext) {
        const res = await getClients({ scheme: schemeId, page, page_size: 1000 });
        clients.push(...res.results);
        hasNext = !!res.next;
        page++;
      }
      return { schemeId, clients };
    };

    setLoading(true);
    Promise.all(schemesToFetch.map(fetchAllClientsForScheme))
      .then(results => {
        if (cancelled) return;
        setSchemeClients(prev => {
          const next = { ...prev };
          results.forEach(({ schemeId, clients }) => { next[schemeId] = clients; });
          return next;
        });
      })
      .catch(err => console.error('Error loading clients for schemes:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // schemeClients is read only to skip cached schemes; depending on it would refetch on every load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(schemeIds), getClients]);

  return { schemeClients, loading };
}

/** The selected clients that belong to one scheme — what that scheme's order is submitted with. */
export function clientIdsForScheme(
  schemeId: number,
  selectedClientIds: number[],
  schemeClients: Record<number, Client[]>,
): number[] {
  const schemeClientIds = new Set((schemeClients[schemeId] || []).map(c => c.id));
  return selectedClientIds.filter(id => schemeClientIds.has(id));
}

/**
 * The selected clients still covered by a new scheme set. Clients belong to a scheme, so
 * any not loaded for one of `schemeIds` are dropped.
 */
export function pruneClientIds(
  selectedClientIds: number[],
  schemeIds: number[],
  schemeClients: Record<number, Client[]>,
): number[] {
  const coveredIds = new Set(schemeIds.flatMap(id => (schemeClients[id] || []).map(c => c.id)));
  return selectedClientIds.filter(id => coveredIds.has(id));
}
